/**
 * m3 echo Frontend - Layout Component
 *
 * Definiert das Haupt-Layout mit Seitenmenü und Content-Bereich.
 * Basierend auf PRD Sektion 8.1.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '../services/billing';
import Chatbot from './Chatbot';
import { UserMenu } from './UserMenu';
import { SupportButton } from './SupportButton';
import NotificationBell from './NotificationBell';
import { useAuthStore, getIdentityScope } from '../stores/useAuthStore';
import { useReducedMotion } from '../hooks/useReducedMotion';

const DRAWER_WIDTH = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const prevPathRef = useRef(location.pathname);
  const contentRef = useRef<HTMLDivElement>(null);

  const animationRef = useRef<Animation | null>(null);

  const animateContent = useCallback(() => {
    const el = contentRef.current;
    if (el && !reduceMotion) {
      // Cancel any in-flight animation before starting a new one
      if (animationRef.current) {
        animationRef.current.cancel();
      }
      animationRef.current = el.animate(
        [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 225, easing: 'cubic-bezier(0.0, 0, 0.2, 1)', fill: 'forwards' }
      );
    }
  }, [reduceMotion]);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      animateContent();
    }
  }, [location.pathname, animateContent]);

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.is_admin || false;

  // Teaser-Analyse nur mit freigeschaltetem Modul (Fehler -> ausblenden)
  const { data: myConfig } = useQuery({
    queryKey: ['billing', 'my-config', getIdentityScope()],
    queryFn: async () => (await billingApi.getMyConfig()).data,
    staleTime: 30_000,
    retry: false,
  });
  const showTeaser = isAdmin || myConfig?.active_features?.teaser_analysis === true;

  /**
   * Menü-Items gemäß PRD
   * Admin menu item is only visible to admin users
   */
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      adminOnly: false,
    },
    {
      text: 'Teaser-Analyse',
      icon: <CampaignIcon />,
      path: '/teaser',
      adminOnly: false,
      hidden: !showTeaser,
    },
    {
      text: 'Admin',
      icon: <AdminPanelSettingsIcon />,
      path: '/admin',
      adminOnly: true,
    },
    {
      text: 'Tools',
      icon: <BuildIcon />,
      path: '/tools',
      adminOnly: true,
    },
    {
      text: 'Billing',
      icon: <ReceiptLongIcon />,
      path: '/billing',
      adminOnly: true,
    },
    {
      text: 'Wiki',
      icon: <MenuBookIcon />,
      path: '/wiki',
      adminOnly: true,
    },
    {
      text: 'Einstellungen',
      icon: <SettingsIcon />,
      path: '/settings',
      adminOnly: false,
    },
  ].filter(item => (!item.adminOnly || isAdmin) && !('hidden' in item && item.hidden));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto', px: 2, py: 1, flexGrow: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#678BA3',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#4D6B80',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#ffffff',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: location.pathname === item.path ? '#ffffff' : '#5A6C7D',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === item.path ? 500 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box sx={{ px: 2, py: 2, borderTop: '1px solid #E5E7EB' }}>
        <SupportButton />
        <UserMenu />
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F5F6F8' }}>
      {/* App Bar (Header) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, color: '#2C3E50' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h4"
            noWrap
            component="div"
            sx={{
              color: '#2C3E50',
              fontWeight: 600,
              flexGrow: 1,
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            m3 <span style={{ color: '#678BA3' }}>echo</span>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#9CA3AF',
              fontSize: '0.875rem',
              fontWeight: 600,
              display: { xs: 'none', sm: 'block' },
              mr: 2,
            }}
          >
            v5.4.0
          </Typography>
          <NotificationBell />
        </Toolbar>
      </AppBar>

      {/* Seitenmenü */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          ...(!isMobile && {
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              borderRight: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: 'column',
            },
          }),
          ...(isMobile && {
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
            },
          }),
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Content-Bereich */}
      <Box
        ref={contentRef}
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          mt: 8,
          backgroundColor: '#F5F6F8',
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
        }}
      >
        {children}
      </Box>

      {/* Chatbot */}
      <Chatbot />
    </Box>
  );
};

export default Layout;
